import { ProFormSelect, ModalForm, ProTable } from '@ant-design/pro-components';
import { App, Button, Drawer, Flex, Popconfirm, Space, Tabs, Tag, Typography } from 'antd';
import PropTypes from 'prop-types';
import { useRef, useState } from 'react';
import {
  queryOperationStores,
  queryStoreNameIssues,
  resolveStoreNameIssues,
} from '../services/operations';

const DOMAIN_LABELS = {
  expense: '经营费用',
  income: '经营收入',
  salary: '工资',
};

const StoreNameIssuesDrawer = ({ onChanged, onClose, open, organizationId }) => {
  const actionRef = useRef(null);
  const { message } = App.useApp();
  const [activeStatus, setActiveStatus] = useState('pending');
  const [associateName, setAssociateName] = useState(null);
  const [counts, setCounts] = useState({ ignoredCount: 0, pendingCount: 0 });
  const [selectedNames, setSelectedNames] = useState([]);

  const refresh = () => {
    setSelectedNames([]);
    actionRef.current?.reload?.();
    onChanged();
  };

  const resolveNames = async (action, names) => {
    try {
      await resolveStoreNameIssues({ action, names, organizationId });
      message.success(action === 'ignore' ? '未归属名称已忽略' : '未归属名称已恢复');
      refresh();
    } catch (error) {
      message.error(error.message);
    }
  };

  const columns = [
    { title: '未归属名称', dataIndex: 'issueName', render: (_, record) => record.name },
    {
      title: '来源',
      dataIndex: 'sourceDomain',
      valueType: 'select',
      fieldProps: {
        allowClear: true,
        options: Object.entries(DOMAIN_LABELS).map(([value, label]) => ({ label, value })),
      },
      render: (_, record) => (
        <Space wrap size={[4, 4]}>
          {record.domains.map((domain) => (
            <Tag key={domain}>{DOMAIN_LABELS[domain] || domain}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: '操作',
      valueType: 'option',
      render: (_, record) => [
        <Button key="associate" type="link" onClick={() => setAssociateName(record.name)}>
          关联门店
        </Button>,
        <Popconfirm
          key="status"
          title={activeStatus === 'pending' ? '确认忽略这个名称？' : '确认恢复这个名称？'}
          onConfirm={() =>
            resolveNames(activeStatus === 'pending' ? 'ignore' : 'restore', [record.name])
          }
        >
          <Button type="link">{activeStatus === 'pending' ? '忽略' : '恢复'}</Button>
        </Popconfirm>,
      ],
    },
  ];

  const batchAction = activeStatus === 'pending' ? 'ignore' : 'restore';
  const batchLabel = activeStatus === 'pending' ? '忽略所选' : '恢复所选';

  return (
    <Drawer
      destroyOnHidden
      open={open}
      size="min(1080px, 100vw)"
      title="管理未归属名称"
      onClose={onClose}
    >
      <Flex vertical gap="middle">
        <Typography.Text type="secondary">
          忽略仅停止提示，不会修改经营事件或报表计算。
        </Typography.Text>
        <Tabs
          activeKey={activeStatus}
          items={[
            { key: 'pending', label: `待处理（${counts.pendingCount}）` },
            { key: 'ignored', label: `已忽略（${counts.ignoredCount}）` },
          ]}
          onChange={(status) => {
            setActiveStatus(status);
            setSelectedNames([]);
          }}
        />
        <ProTable
          actionRef={actionRef}
          columns={columns}
          headerTitle={
            <Button
              disabled={selectedNames.length === 0}
              onClick={() => resolveNames(batchAction, selectedNames)}
            >
              {batchLabel}
            </Button>
          }
          params={{ organizationId, status: activeStatus }}
          request={async ({ issueName, ...params }) => {
            const result = await queryStoreNameIssues({ ...params, name: issueName });
            setCounts({
              ignoredCount: result.meta.ignoredCount || 0,
              pendingCount: result.meta.pendingCount || 0,
            });
            return result;
          }}
          rowKey="name"
          rowSelection={{
            selectedRowKeys: selectedNames,
            onChange: setSelectedNames,
          }}
          search={{ labelWidth: 72 }}
        />
      </Flex>
      <ModalForm
        open={Boolean(associateName)}
        title={`关联门店：${associateName || ''}`}
        modalProps={{ destroyOnHidden: true, onCancel: () => setAssociateName(null) }}
        onFinish={async ({ storeId }) => {
          try {
            await resolveStoreNameIssues({
              action: 'associate',
              names: [associateName],
              organizationId,
              storeId,
            });
            message.success('未归属名称已关联门店');
            setAssociateName(null);
            refresh();
            return true;
          } catch (error) {
            message.error(error.message);
            return false;
          }
        }}
      >
        <ProFormSelect
          name="storeId"
          label="关联门店"
          request={async ({ keyWords }) => {
            const result = await queryOperationStores({
              current: 1,
              includeAliasHealth: 'false',
              name: keyWords,
              organizationId,
              pageSize: 50,
            });
            return result.data.map((store) => ({
              label: `${store.code} ${store.name}`,
              value: store.id,
            }));
          }}
          rules={[{ required: true, message: '请选择需要关联的门店' }]}
          fieldProps={{ filterOption: false, showSearch: true }}
        />
      </ModalForm>
    </Drawer>
  );
};

StoreNameIssuesDrawer.propTypes = {
  onChanged: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  organizationId: PropTypes.string.isRequired,
};

export default StoreNameIssuesDrawer;
